import React, { useState, useEffect } from "react";
import { adsAPI } from "../services/api";
import { useApp } from "../context/AppContext";
import { formatPrice, formatDate } from "../utils/helpers";

export default function AdsAdmin() {
  const { currentUser } = useApp();
  const [ads, setAds]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]     = useState("pending");
  const [messageDrafts, setMessageDrafts] = useState({});
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => { loadAds(); }, []);

  const loadAds = async () => {
    setLoading(true);
    try {
      const { ads } = await adsAPI.getAll();
      setAds(ads || []);
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove    = async (id) => { await adsAPI.approve(id);    loadAds(); };
  const handleReject     = async (id) => { if(!window.confirm("Rechazar? El pago se devuelve.")) return; await adsAPI.reject(id);     loadAds(); };
  const handleDeactivate = async (id) => { await adsAPI.deactivate(id); loadAds(); };

  const isExpired = (ad) => ad.end_date && new Date(ad.end_date) < new Date();
  const isThisMonth = (dateStr) => {
    const d = new Date(dateStr), now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  const getBadge = (ad) => {
    if (ad.status === "pending")  return { text: "⏳ Pendiente",   bg: "#fff8e1", color: "#b7830a" };
    if (ad.status === "rejected") return { text: "❌ Rechazada",   bg: "#FEE",    color: "#c0392b" };
    if (ad.status === "inactive") return { text: "⚪ Desactivada", bg: "#f0f0f0", color: "#888" };
    if (ad.status === "approved") {
      if (ad.payment_status !== "paid") return { text: "💳 Falta pago", bg: "#e8f4fd", color: "#009ee3" };
      if (isExpired(ad))                return { text: "⚪ Vencida",    bg: "#f0f0f0", color: "#888" };
      return { text: "✅ Activa", bg: "#E1F5EE", color: "#0F6E56" };
    }
    return { text: ad.status, bg: "#f0f0f0", color: "#888" };
  };

  const handleSendMessage = async (adId) => {
    const message = (messageDrafts[adId] || "").trim();
    if (!message) return;
    setSendingId(adId);
    try {
      await adsAPI.sendMessage(adId, message);
      setMessageDrafts(prev => ({ ...prev, [adId]: "" }));
    } catch(e) {
      alert(e.message);
    } finally {
      setSendingId(null);
    }
  };

  if (currentUser?.role !== "admin") return null;

  const filtered = ads.filter(a =>
    tab === "pending"  ? a.status === "pending"  :
    tab === "active"   ? a.status === "approved" :
    a.status === "rejected" || a.status === "inactive"
  );

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Gestion de publicidades</h1>

      <div style={s.stats}>
        {[
          { label:"Pendientes", value: ads.filter(a=>a.status==="pending").length,  color:"#f59e0b" },
          { label:"Activas",    value: ads.filter(a=>a.status==="approved" && a.payment_status==="paid" && !isExpired(a)).length, color:"#1D9E75" },
          { label:"Ingresos del mes", value: formatPrice(ads.filter(a => a.payment_status==="paid" && a.start_date && isThisMonth(a.start_date)).reduce((sum,a) => sum + Number(a.amount || 0), 0)), color:"#3b82f6" },
        ].map(st => (
          <div key={st.label} style={s.statCard}>
            <p style={{...s.statValue,color:st.color}}>{st.value}</p>
            <p style={s.statLabel}>{st.label}</p>
          </div>
        ))}
      </div>

      <div style={s.tabs}>
        {[["pending","Pendientes"],["active","Activas"],["rejected","Rechazadas"]].map(([id,label]) => (
          <button key={id} style={{...s.tab,...(tab===id?s.tabActive:{})}} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {loading ? <p style={s.empty}>Cargando...</p> : filtered.length === 0 ? (
        <p style={s.empty}>No hay publicidades en esta categoria.</p>
      ) : (
        <div style={s.list}>
          {filtered.map(ad => (
            <div key={ad.id} style={s.card}>
              <div style={s.cardTop}>
                {ad.image_url && <img src={ad.image_url} alt={ad.business_name} style={s.adImg} />}
                <div style={s.adInfo}>
                  <p style={s.adTitle}>{ad.business_name}</p>
                  <p style={s.adDesc}>{ad.description}</p>
                  {ad.website       && <p style={s.adMeta}>🌐 {ad.website}</p>}
                  <p style={s.adMeta}>👤 {ad.contact_name}</p>
                  <p style={s.adMeta}>📧 {ad.contact_email}</p>
                  {ad.contact_phone && <p style={s.adMeta}>📱 {ad.contact_phone}</p>}
                  <p style={s.adMeta}>📅 Solicitado el {formatDate(ad.created_at)}</p>
                  {ad.payment_status === "paid" && ad.start_date && (
                    <p style={{...s.adMeta,color:"#1D9E75",fontWeight:600}}>💳 Pagado el {formatDate(ad.start_date)} — {formatPrice(ad.amount)}</p>
                  )}
                  {ad.end_date      && <p style={s.adMeta}>⏱️ Activa hasta {formatDate(ad.end_date)}</p>}
                </div>
              </div>
              <div style={s.cardActions}>
                <span style={{...s.statusBadge, background: getBadge(ad).bg, color: getBadge(ad).color}}>
                  {getBadge(ad).text}
                </span>
                <div style={s.btnGroup}>
                  {ad.status === "pending"  && <><button style={s.btnApprove} onClick={() => handleApprove(ad.id)}>✅ Aprobar</button><button style={s.btnReject} onClick={() => handleReject(ad.id)}>❌ Rechazar</button></>}
                  {ad.status === "approved" && !isExpired(ad) && <button style={s.btnReject} onClick={() => handleDeactivate(ad.id)}>Desactivar</button>}
                </div>
              </div>
              <div style={s.messageRow}>
                <input
                  style={s.messageInput}
                  placeholder="Escribile un mensaje al anunciante..."
                  value={messageDrafts[ad.id] || ""}
                  onChange={e => setMessageDrafts(prev => ({ ...prev, [ad.id]: e.target.value }))}
                />
                <button
                  style={s.messageBtn}
                  disabled={sendingId === ad.id || !(messageDrafts[ad.id] || "").trim()}
                  onClick={() => handleSendMessage(ad.id)}
                >
                  {sendingId === ad.id ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  page:{maxWidth:900,margin:"0 auto",padding:"24px 16px"},
  heading:{fontSize:22,fontWeight:600,marginBottom:20},
  stats:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12,marginBottom:24},
  statCard:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:12,padding:"16px 20px",textAlign:"center"},
  statValue:{fontSize:24,fontWeight:700,marginBottom:4},
  statLabel:{fontSize:12,color:"#888"},
  tabs:{display:"flex",gap:8,marginBottom:20},
  tab:{padding:"8px 16px",borderRadius:8,border:"0.5px solid #ddd",background:"#fff",cursor:"pointer",fontSize:13,color:"#555"},
  tabActive:{background:"#1D9E75",color:"#fff",borderColor:"#1D9E75",fontWeight:500},
  list:{display:"flex",flexDirection:"column",gap:16},
  card:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:12,padding:"20px"},
  cardTop:{display:"flex",gap:16,marginBottom:16},
  adImg:{width:120,height:90,objectFit:"cover",borderRadius:8,flexShrink:0},
  adInfo:{flex:1},
  adTitle:{fontSize:16,fontWeight:600,color:"#111",marginBottom:6},
  adDesc:{fontSize:13,color:"#555",lineHeight:1.5,marginBottom:8},
  adMeta:{fontSize:12,color:"#888",marginBottom:3},
  cardActions:{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,borderTop:"0.5px solid #f0f0f0",paddingTop:14},
  statusBadge:{padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:500},
  btnGroup:{display:"flex",gap:8},
  btnApprove:{background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer"},
  btnReject:{background:"#FEE",color:"#c0392b",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,cursor:"pointer"},
  empty:{color:"#888",fontSize:14,padding:"40px",textAlign:"center"},
  messageRow:{display:"flex",gap:8,marginTop:12,borderTop:"0.5px solid #f0f0f0",paddingTop:12},
  messageInput:{flex:1,border:"0.5px solid #ddd",borderRadius:8,padding:"8px 12px",fontSize:13},
  messageBtn:{background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"},
};
